import { useState } from 'react'
import { ErrorBanner, Metric } from '@/components/AppLayout'
import { ResetEventCard } from '@/features/events/ResetEventCard'
import { useAuth } from '@/features/auth/useAuth'
import { useAsync } from '@/hooks/useAsync'
import { getForm, listAttendees, listSubmissions, updateEvent } from '@/lib/queries'
import { useEventContext } from './eventContext'

export function EventOverviewPage() {
  const { event, reloadEvent } = useEventContext()
  const { memberships } = useAuth()
  const isOwner = memberships.some(
    (m) => m.organization.id === event.organization_id && m.role === 'owner'
  )
  const attendees = useAsync(() => listAttendees(event.id), [event.id])
  const submissions = useAsync(() => listSubmissions(event.id), [event.id])
  const form = useAsync(() => getForm(event.id), [event.id])

  const [name, setName] = useState(event.name)
  const [venue, setVenue] = useState(event.venue ?? '')
  const [displayOrgName, setDisplayOrgName] = useState(event.display_org_name ?? '')
  const [logoUrl, setLogoUrl] = useState(event.logo_url ?? '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const active = (attendees.data ?? []).filter((a) => a.status === 'active')
  const visible = active.filter((a) => a.is_visible)

  async function handleSave() {
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    try {
      await updateEvent(event.id, {
        name: name.trim(),
        venue: venue.trim() === '' ? null : venue.trim(),
        display_org_name: displayOrgName.trim() === '' ? null : displayOrgName.trim(),
        logo_url: logoUrl.trim() === '' ? null : logoUrl.trim(),
      })
      setSaved(true)
      reloadEvent()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the event')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="stack">
      <div className="metric-grid">
        <Metric label="Attendees" value={active.length} />
        <Metric label="On the form" value={visible.length} />
        {/* Entries, not people: a repeat submitter shows up more than once,
            and the Form tab is where that gets sorted out. */}
        <Metric label="Submissions" value={(submissions.data ?? []).length} />
        <Metric label="Form" value={form.data === null ? '—' : form.data.status} />
      </div>

      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault()
          void handleSave()
        }}
      >
        <h2>Event details</h2>

        <div className="field">
          <label className="field-label" htmlFor="name">
            Name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
            }}
            required
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="venue">
            Venue
          </label>
          <input
            id="venue"
            type="text"
            value={venue}
            onChange={(e) => {
              setVenue(e.target.value)
            }}
            placeholder="The Wine Bar, 12 High Street"
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="displayOrgName">
            Organisation name on the form
          </label>
          <p className="field-hint">
            Shown to attendees instead of your organization name. Leave blank to use the default.
          </p>
          <input
            id="displayOrgName"
            type="text"
            value={displayOrgName}
            onChange={(e) => {
              setDisplayOrgName(e.target.value)
            }}
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="logoUrl">
            Logo URL
          </label>
          <p className="field-hint">Must be https. Shown at the top of the public form.</p>
          <input
            id="logoUrl"
            type="url"
            value={logoUrl}
            onChange={(e) => {
              setLogoUrl(e.target.value)
            }}
            placeholder="https://example.com/logo.png"
          />
        </div>

        {saveError !== null && <ErrorBanner message={saveError} />}
        {saved && <p className="banner banner-success">Saved.</p>}

        <div className="row btn-group-stack">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
      <ResetEventCard
        eventId={event.id}
        eventName={event.name}
        isOwner={isOwner}
        onReset={() => {
          attendees.reload()
          submissions.reload()
          form.reload()
          reloadEvent()
        }}
      />
    </div>
  )
}
